/**
 * The last few round trips to each peer, and the middle one.
 *
 * The heartbeat measures once a second and every one of those samples is honest, but a single
 * retransmit behind a lost packet reads as a link four times worse than it is. A median throws that
 * one away outright instead of dragging the reading along behind it for several beats, which is
 * what an average would do, and a band that flickers between two words is worse to read than one
 * that is five seconds stale.
 *
 * Five samples, so the window is five seconds at the heartbeat's cadence: long enough that two lost
 * beats in a row cost nothing, short enough that a link that genuinely went bad says so before a
 * player has finished wondering.
 *
 * No clock. A sample arrives or it does not, and the window holds what it was given.
 */

import type { PeerId } from '@/net/transportContract';

/** Odd, so the median is a sample that was actually measured rather than two averaged. */
export const RTT_WINDOW_SAMPLES = 5;

export interface RttWindow {
  /** Null is not a measurement and leaves the window as it was. */
  sample(id: PeerId, rttMs: number | null): void;
  /** The middle of what is held, or null before anything has come back from this peer. */
  medianOf(id: PeerId): number | null;
  /** A peer that left. Its samples go with it, so a returning peer starts from nothing. */
  forget(id: PeerId): void;
}

export function createRttWindow(): RttWindow {
  const samples = new Map<PeerId, number[]>();

  return {
    sample(id: PeerId, rttMs: number | null): void {
      if (rttMs === null) return;
      const held = samples.get(id) ?? [];
      held.push(rttMs);
      if (held.length > RTT_WINDOW_SAMPLES) held.shift();
      samples.set(id, held);
    },

    medianOf(id: PeerId): number | null {
      const held = samples.get(id);
      if (held === undefined || held.length === 0) return null;
      // Sorted on a copy: the window itself stays in arrival order, so the oldest sample is the
      // one that leaves next however fast or slow it happened to be.
      const sorted = [...held].sort((left, right) => left - right);
      return sorted[(sorted.length - 1) >> 1] ?? null;
    },

    forget(id: PeerId): void {
      samples.delete(id);
    },
  };
}
